<script setup lang="ts">
import { ref, computed, onMounted, onActivated, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import ContractTable from "@/components/models/practice/table/ContractTable.vue";
import MsoTable from "@/components/models/practice/table/MsoTable.vue";
import OrthoCompaniesTable from "@/components/models/company/table/OrthoCompaniesTable.vue";
import GPCompaniesTable from "@/components/models/company/table/GPCompaniesTable.vue";
import GPPracticeTable from "@/components/models/practice/table/GPPracticeTable.vue";
import OrthoPracticeTable from "@/components/models/practice/table/OrthoPracticeTable.vue";
import AssistantTab from "@/components/models/assistant/AssistantTab.vue";
import StaffTable from "@/components/models/practice/table/StaffTable.vue";
import ReportTable from "@/components/models/report/table/ReportTable.vue";
import { useDisplay } from "vuetify/framework";
import { ETabs } from "@/api/shared/administrative.ts";
import { useAbility } from "@casl/vue";
import {
  useAdministrationTabPermissions,
  useCanAccessAdministration,
} from "@/composables/shared/useSectionPermissions";
import PageSearchFilter from "@/components/shared/table/PageSearchFilter.vue";
import { ORDERING_QUERY_KEY } from "@/composables/shared/filters/useUrlSort";
import { useSettings } from "@/composables/shared/useSettings";

definePage({
  meta: {
    title: "generic.entity.administrative",
    checkPrivileges: (to: any) => {
      if (!useCanAccessAdministration().value) return false;
      if (!to.params.tab) return true;
      const privileges = useAdministrationTabPermissions().value;
      const tab = to.params.tab as keyof typeof privileges;
      return privileges[tab];
    },
  },
});

const routeParams = computed((): { tab?: string } => route.params as { tab?: string });
const { can } = useAbility();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { smAndUp, mdAndUp } = useDisplay();
const { isDarkMode } = useSettings();
const activeTab = ref(ETabs.ASSISTANT);

const buttonAvatarColor = () => {
  if (!mdAndUp.value) return "transparent";
  return isDarkMode.value ? "surface" : "white";
};

const buttonIconColor = () => {
  if (!mdAndUp.value) return isDarkMode.value ? "on-primary" : "white";
  return "secondary";
};

const tabSettings = computed(
  (): Record<ETabs, Record<string, any>> => ({
    [ETabs.ASSISTANT]: {
      title: t("generic.entity.assistant"),
      tableComponent: AssistantTab,
      search: {
        hidden: true,
      },
      style: {
        icon: "mdi-assistant",
        classes:
          "tw:!bg-gradient-to-r tw:from-teal-400 tw:to-blue-600 tw:!text-white tw:!px-10 tw:!font-bold tw:!text-[1rem] tw:shadow-none tw:normal-case",
      },
      permissions: {
        button: true,
        table: true,
      },
    },
    [ETabs.CONTRACTS]: {
      search: {
        hidden: true,
        disabled: true,
      },
      title: t("generic.entity.contract", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.contract"),
        }),
        to: { path: "/contracts/add/" },
        icon: "mdi-briefcase-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__CONTRACT),
      },
      tableComponent: ContractTable,
    },

    [ETabs.MSOS]: {
      title: t("generic.entity.mso", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.mso"),
        }),
        to: { path: "/msos/add/" },
        icon: "mdi-briefcase-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__MSO),
      },
      search: {
        placeholder: t("generic.tables.searchByNameOrWebsite"),
      },
      tableComponent: MsoTable,
    },
    [ETabs.ORTHO_PRACTICES]: {
      title: t("generic.entity.orthoCompany", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.orthoCompany"),
        }),
        to: { path: "/companies/add/", query: { type: "ortho" } },
        icon: "mdi-office-building-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__COMPANY),
      },
      search: {
        placeholder: t("generic.tables.searchByName"),
      },
      tableComponent: OrthoCompaniesTable,
    },
    [ETabs.ORTHO_OFFICES]: {
      title: t("generic.entity.orthoPractice", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.orthoPractice"),
        }),
        to: { path: "/practices/add/", query: { type: "ortho" } },
        icon: "mdi-office-building-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__PRACTICE),
      },
      search: {
        placeholder: t("generic.tables.searchByNameOrWebsite"),
      },
      tableComponent: OrthoPracticeTable,
      // search: {
      //  disabled: true,
      // },
    },
    [ETabs.GP_PRACTICES]: {
      title: t("generic.entity.gpCompany", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.gpCompany"),
        }),
        to: { path: "/companies/add/", query: { type: "gp" } },
        icon: "mdi-office-building-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__COMPANY),
      },
      search: {
        placeholder: t("generic.tables.searchByName"),
      },
      tableComponent: GPCompaniesTable,
    },
    [ETabs.GP_OFFICES]: {
      title: t("generic.entity.gpPractice", 2),
      button: {
        title: t(`generic.action.addEntity`, {
          entity: t("generic.entity.gpPractice"),
        }),
        to: { path: "/practices/add/", query: { type: "gp" } },
        icon: "mdi-office-building-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__PRACTICE),
      },
      search: {
        placeholder: t("generic.tables.searchByNameOrWebsite"),
      },
      tableComponent: GPPracticeTable,
    },
    [ETabs.MEMBERS]: {
      title: t("generic.entity.staff", 2),
      button: {
        title: t("generic.action.addEntity", {
          entity: t("generic.entity.staff", 0),
        }),
        to: { path: "/practice-staff/add/" },
        icon: "mdi-account-plus-outline",
      },
      permissions: {
        button: can($permActions.CREATE, $permSubjects.PRACTICE__PRACTICESTAFF),
      },
      search: {
        placeholder: t("generic.tables.searchByNameOrEmail"),
      },
      tableComponent: StaffTable,
    },
    [ETabs.REPORTS]: {
      title: t("generic.entity.report", 2),
      // The list endpoint has no search filter, so there is nothing to type into.
      search: {
        hidden: true,
      },
      // Read-only tab: reports are created outside this UI, so there is no top button.
      tableComponent: ReportTable,
    },
  })
);

const tabPrivileges = useAdministrationTabPermissions();

const visibleTabs = computed(() => {
  const privileges = tabPrivileges.value;
  const result = Object.entries(tabSettings.value).filter(([key]) => privileges[key as ETabs]);
  return result;
});

const handleTabPath = () => {
  if (routeParams.value.tab && routeParams.value.tab in tabSettings.value) {
    activeTab.value = routeParams.value.tab as ETabs;
  } else {
    router.replace({
      name: "/administrative/[[tab]]",
      params: { ...route.params, tab: activeTab.value },
      query: route.query,
    });
  }
};

watch(
  () => routeParams.value.tab,
  (newTab) => {
    if (newTab && newTab in tabSettings.value) {
      activeTab.value = newTab as ETabs;
    }
  }
);

onMounted(() => handleTabPath());
onActivated(() => handleTabPath());

const handleTabClick = (tabId: ETabs) => {
  const query = { ...route.query };
  if (tabId !== activeTab.value) {
    // Each tab renders a different table with its own sortable columns, so a
    // carried-over `?ordering=` would apply a stale/invalid sort — drop it on switch.
    delete query[ORDERING_QUERY_KEY];
  }
  router.push({
    name: "/administrative/[[tab]]",
    params: { ...route.params, tab: tabId },
    query,
  });
};
</script>

<template>
  <div
    class="tw:!mt-8"
    data-testid="administrative"
  >
    <v-tabs
      :model-value="activeTab"
      hide-slider
      density="comfortable"
    >
      <v-tab
        v-for="[tabId, tab] in visibleTabs"
        :key="`tab-${tabId}`"
        :value="tabId"
        :text="tab.title"
        variant="flat"
        :base-color="tabId === activeTab ? 'liteGreen4' : 'liteDef3'"
        class="tw:!rounded-t-xl tw:!mr-1"
        :class="tab.style?.classes"
        :prepend-icon="tab.style?.icon"
        data-testid="administrative-tab"
        @click="handleTabClick(tabId as ETabs)"
      />
    </v-tabs>
    <v-divider />
    <v-tabs-window
      v-model="activeTab"
      class="tw:!h-full"
    >
      <v-tabs-window-item
        v-for="[tabId, tab] in visibleTabs"
        :key="`tab-window-${tabId}`"
        :value="tabId"
        class="tw:!h-full tw:!w-full"
      >
        <div class="tw:!h-full tw:!w-full">
          <div
            class="tw:flex tw:flex-col tw:md:flex-row tw:items-stretch tw:md:items-center tw:gap-4 tw:!mt-8"
          >
            <div
              v-if="'button' in tab"
              class="tw:shrink-0"
            >
              <v-btn
                v-if="tab.permissions.button"
                variant="flat"
                color="secondary"
                class="tw:!rounded-xl tw:w-full tw:md:w-[300px] tw:!h-[56px] tw:md:!h-[80px] tw:dark:!text-on-primary"
                :to="tab.button.to"
                data-testid="administrative-tab-button"
              >
                <span
                  v-if="smAndUp"
                  class="tw:font-bold tw:text-[15px]"
                >
                  {{ tab.button.title }}
                </span>
                <span class="tw:md:!ml-4">
                  <v-avatar
                    :color="buttonAvatarColor()"
                    size="38"
                  >
                    <v-icon
                      size="24"
                      :icon="tab.button.icon"
                      :color="buttonIconColor()"
                    />
                  </v-avatar>
                </span>
              </v-btn>
            </div>
            <page-search-filter
              v-if="!tab.search?.hidden"
              :disabled="tab.search?.disabled"
              :placeholder="tab.search?.placeholder"
            />
          </div>
          <div class="tw:!mt-7 tw:!h-full tw:!w-full">
            <component
              :is="tab.tableComponent"
              v-if="activeTab === tabId"
            />
          </div>
        </div>
      </v-tabs-window-item>
    </v-tabs-window>
  </div>
</template>

<style>
.custom-search-field .v-field {
  padding-right: 0 !important;
}

:deep(.v-text-field--outlined) fieldset {
  border-color: rgba(192, 0, 250, 0.986);
}
</style>
